import { ProjectInfo } from '../models/project-info';

/**
 * Project liveness for the selection pane — SERVER-DECIDED.
 *
 * Liveness is no longer computed on the client. DataServices returns an `isLive`
 * boolean on GetProjectlist, derived from the cure's finished flag, a 48 h bound
 * and latest-per-CMS.
 *
 * Why the client stopped deciding
 * -------------------------------
 * Every client-side rule this pane has had was a proxy for activity rather than a
 * measure of it:
 *
 *   flat 6 h / 7 d window   counted a cure that ended 51 minutes ago as live, so
 *                           one CMS showed two live projects
 *   k * MI recency          fixed that, but still cannot distinguish "streaming"
 *                           from "stopped moments ago", and cannot see a FIELD
 *                           DISCONNECT (rig offline mid-cure still looks live for
 *                           k intervals) or a COLD START (cure begun, no telemetry
 *                           yet, so it looks dead)
 *
 * The server knows the cure's finished flag and which project is the latest for
 * each CMS, so it can answer correctly in all four cases. The client's job is now
 * to render that answer, not to re-derive it.
 *
 * What remains here
 * -----------------
 * Only the TIMESTAMP helpers, which are still client concerns: sort order and the
 * "last seen" column read the telemetry-derived `lastTelemetryEpochMs` (falling
 * back to the drift-prone stored LTR when the server has not enriched a row).
 * `MI` is still carried on the model but is no longer used for liveness.
 */

/** The fields the pane reads for liveness and ordering. */
export interface LivenessFields {
  /** SERVER-DECIDED liveness. The ONLY input to isProjectLive. */
  isLive?: boolean | null;
  /** Telemetry-derived epoch (ms) — used for sort and "last seen", not liveness. */
  lastTelemetryEpochMs?: number | null;
  /** Stored last-telemetry timestamp — the legacy, drift-prone fallback. */
  LastTelemetryReceived?: string | null;
}

/**
 * Newest-telemetry epoch in ms, or null when the project has none.
 *
 * Prefers the telemetry-derived epoch when present; otherwise parses the legacy
 * string. Returns null rather than 0 so "no telemetry yet" stays distinguishable
 * from "telemetry at the unix epoch" — that distinction is what keeps a
 * brand-new cure visible instead of being filtered out.
 */
export function lastTelemetryEpoch(
  project: LivenessFields | null | undefined
): number | null {
  const derived = project?.lastTelemetryEpochMs;
  if (typeof derived === 'number' && Number.isFinite(derived) && derived > 0) {
    return derived;
  }
  const legacy = project?.LastTelemetryReceived;
  if (!legacy) {
    return null;
  }
  const parsed = new Date(legacy).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * True when the SERVER says this project is a live cure.
 *
 * Absent isLive means NOT live. That is deliberate: liveness is server-decided, so
 * guessing on the client is exactly what this redesign removes. A row the server
 * has not enriched (older API build, or outside the enrichment window) therefore
 * renders under Historical — visible, just not claimed to be live.
 */
export function isProjectLive(
  project: LivenessFields | null | undefined
): boolean {
  return project?.isLive === true;
}

/**
 * Split a list into live and historical, losing nothing.
 *
 * The two sides are exhaustive and disjoint by construction: every project lands
 * in exactly one. That is the property that guarantees a project which is in the
 * API response is always rendered somewhere — including a brand-new cure whose
 * telemetry epoch has not been stamped yet.
 */
export function partitionProjects<T extends LivenessFields>(
  projects: readonly T[]
): { live: T[]; historical: T[] } {
  const live: T[] = [];
  const historical: T[] = [];
  for (const project of projects ?? []) {
    if (isProjectLive(project)) {
      live.push(project);
    } else {
      historical.push(project);
    }
  }
  return { live, historical };
}

/** Newest-telemetry-first, with unknown-epoch projects last. */
export function byNewestTelemetry(
  a: LivenessFields,
  b: LivenessFields
): number {
  const ea = lastTelemetryEpoch(a);
  const eb = lastTelemetryEpoch(b);
  if (ea === null && eb === null) return 0;
  if (ea === null) return 1;
  if (eb === null) return -1;
  return eb - ea;
}

/** Convenience for callers holding ProjectInfo rows. */
export type LiveProject = ProjectInfo & LivenessFields;
